import { createMemo } from "solid-js";
import { organization, useActiveMemberRole, useSession } from "../../lib/auth-client";

type ProjectAction = "create" | "list" | "update" | "delete" | "toggleSpotlight";
type OrgAction = "update" | "delete";
type RoleType = "owner" | "admin" | "member";

function checkProjectPermission(role: string | undefined, action: ProjectAction): boolean {
  if (!role) return false;
  const result = organization.checkRolePermission({
    role: role as RoleType,
    permissions: { project: [action] } as any,
  });
  return result === true;
}

function checkOrgPermission(role: string | undefined, action: OrgAction): boolean {
  if (!role) return false;
  const result = organization.checkRolePermission({
    role: role as RoleType,
    permissions: { organization: [action] },
  });
  return result === true;
}

export function useProjectPermissions() {
  const sessionQuery = useSession();
  const activeMemberRoleQuery = useActiveMemberRole();

  const role = createMemo(() => activeMemberRoleQuery().data?.role);
  const userIsAdmin = createMemo(() => sessionQuery().data?.user.role === "admin");

  const canCreate = createMemo(() => userIsAdmin() || checkProjectPermission(role(), "create"));
  const canList = createMemo(() => userIsAdmin() || checkProjectPermission(role(), "list"));
  const canUpdate = createMemo(() => userIsAdmin() || checkProjectPermission(role(), "update"));
  const canDelete = createMemo(() => userIsAdmin() || checkProjectPermission(role(), "delete"));
  const canToggleSpotlight = createMemo(() => userIsAdmin() || checkProjectPermission(role(), "toggleSpotlight"));
  const canManageOrg = createMemo(() => userIsAdmin() || checkOrgPermission(role(), "update"));

  return {
    canCreate,
    canList,
    canUpdate,
    canDelete,
    canToggleSpotlight,
    canManageOrg,
    isPermissionsPending: createMemo(() => activeMemberRoleQuery().isPending || sessionQuery().isPending),
  };
}
